import { toMonthYear } from '@nx-ng/material';
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  EventEmitter,
  Input,
  OnInit,
  Output,
  forwardRef,
  Inject,
} from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';
import {
  DateAdapter,
  MAT_DATE_FORMATS,
  MAT_DATE_LOCALE,
} from '@angular/material/core';
import { MatDatepicker } from '@angular/material/datepicker';
import { I18NEXT_SERVICE, ITranslationService } from 'angular-i18next';
import { enGB } from 'date-fns/locale';
import { DateFnsAdapter } from '@angular/material-date-fns-adapter';
import { MONTH_YEAR_FORMAT_FOR_DATEPICKER } from '../datepicker/material-date-format';

@Component({
  selector: 'nx-ng-datepicker-month-year',
  templateUrl: './datepicker-month-year.component.html',
  styleUrls: ['./datepicker-month-year.component.scss'],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      multi: true,
      useExisting: forwardRef(() => DatepickerMonthYearComponent),
    },
    {
      provide: MAT_DATE_FORMATS,
      useValue: MONTH_YEAR_FORMAT_FOR_DATEPICKER,
    },

    {
      provide: MAT_DATE_LOCALE,
      useValue: enGB,
    },
    {
      provide: DateAdapter,
      useClass: DateFnsAdapter,
      deps: [MAT_DATE_LOCALE],
    },
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class DatepickerMonthYearComponent
  implements ControlValueAccessor, OnInit
{
  @Input()
  date: Date | null = null;

  @Input()
  minDate: Date | null = null;

  @Output()
  dateChanged: EventEmitter<Date | null> = new EventEmitter<Date | null>();

  @Input()
  id = '';

  @Input()
  label = '';

  @Input()
  placeholder = 'date_placeholder';

  @Input()
  errorText = '';

  @Input()
  isRequired = false;

  private localValue: Date | null = null;
  private touched = false;

  constructor(private changeDetectorRef: ChangeDetectorRef) {}

  ngOnInit(): void {
    this.writeValue(this.date);
  }

  set value(value: Date | null) {
    this.localValue = value;
    this.onChange(value);
  }

  get value(): Date | null {
    return this.localValue;
  }

  onChange = (value: Date | null) => {
    this.dateChanged.emit(value);
  };

  onTouch = () => {};

  writeValue(value: Date | null) {
    this.localValue = value;
    if (this.changeDetectorRef) {
      this.changeDetectorRef.markForCheck();
    }
  }

  registerOnChange(fn: any) {
    this.onChange = fn;
  }

  registerOnTouched(fn: any) {
    this.onTouch = fn;
  }

  get errorId() {
    return `${this.id}-error`;
  }

  get hasError() {
    return this.errorText?.length > 0;
  }

  markAsTouched() {
    if (!this.touched) {
      this.touched = true;
      this.onTouch();
    }
  }

  setMonthAndYear(
    normalizedMonthAndYear: Date,
    datepicker: MatDatepicker<Date>
  ) {
    this.value = toMonthYear(normalizedMonthAndYear);
    datepicker.close();
  }
}
